import React, { useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { NavLink, useNavigate } from "react-router-dom";
import { Table } from "react-bootstrap";
import { Copy, Trash, Pencil } from "react-bootstrap-icons";

import { useUiFeedback } from "../../../../context/ui-feedback/ui-feedback";
import { createConfirmFeedback } from "../../../../context/ui-feedback/action";
import { CellActions } from "../../../common/cell-actions/cell-actions";
import { Loading } from "../../../common/loading/loading";
import { CopyModel } from "./copy-model";

export const TemplateTable = ({
  rowNumberInit,
  templates,
  hasError,
  handleTemplateCopy,
  handleTemplateDelete,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { uiFeedbackDispatch } = useUiFeedback();

  const [copyConfirm, setCopyConfirm] = useState(null);

  const TableBody = ({ templates, rowNumberInit }) => {
    if (templates.length === 0) {
      return (
        <tr>
          <td colSpan={7}>
            <span className="text-danger">
              <b>
                <FormattedMessage id="data.template.warning.notfoundtemplates" />
              </b>
            </span>
          </td>
        </tr>
      );
    }

    const TemplateStatus = ({ draft, deleted }) => {
      if (draft) {
        return <FormattedMessage id="data.template.data.status.value.draft" />;
      } else if (deleted) {
        return (
          <FormattedMessage id="data.template.data.status.value.deleted" />
        );
      } else {
        return <FormattedMessage id="data.template.data.status.value.active" />;
      }
    };

    const setDeleteConfirm = (template) => {
      createConfirmFeedback(
        uiFeedbackDispatch,
        intl.formatMessage({
          id: "page.templates.label.deletetemplate.title",
        }),
        intl.formatMessage(
          {
            id: "page.templates.label.deletetemplate.question",
          },
          { name: template?.name },
        ),
        () => {
          handleTemplateDelete(template);
        },
      );
    };

    return templates.map((template, index) => {
      const buttons = [];
      buttons.push({
        variant: "secondary",
        title: intl.formatMessage({ id: "page.templates.button.copytemplate" }),
        icon: <Copy />,
        action: () => {
          setCopyConfirm(template);
        },
      });
      if (!template.deleted) {
        buttons.push({
          variant: "secondary",
          title: intl.formatMessage({
            id: "page.templates.button.deletetemplate",
          }),
          icon: <Trash />,
          action: () => {
            setDeleteConfirm(template);
          },
        });
        buttons.push({
          variant: "secondary",
          title: intl.formatMessage({
            id: "page.templates.button.edittemplate",
          }),
          icon: <Pencil />,
          action: () => {
            navigate(`/edittemplate/${template.id}`);
          },
        });
      }

      return (
        <tr key={template.id}>
          <td className="col-rownumber">{rowNumberInit + index + 1}.</td>
          <td className="col-name">
            <NavLink to={`/edittemplate/${template.id}`}>
              {template.name}
            </NavLink>
          </td>
          <td className="col-status">
            <TemplateStatus draft={template.draft} deleted={template.deleted} />
          </td>
          <td className="col-version">{template.version}</td>
          <td className="col-created-user">
            {template.createdUser.name} ({template.createdUser.email})
          </td>
          <td className="col-created-date">{template.createdDate}</td>
          <td className="col-actions">
            <CellActions buttons={buttons} />
          </td>
        </tr>
      );
    });
  };

  const LoadingBody = ({ hasError }) => (
    <tr>
      <td colSpan={7}>
        {hasError ? (
          <span className="text-danger">
            <FormattedMessage id="data.template.exception.listTemplate" />
          </span>
        ) : (
          <Loading />
        )}
      </td>
    </tr>
  );

  return (
    <>
      <div className="template-table-component table-responsive">
        <Table striped bordered>
          <thead>
            <tr>
              <th className="col-rownumber">#</th>
              <th className="col-name">
                <FormattedMessage id="data.template.data.name" />
              </th>
              <th className="col-status">
                <FormattedMessage id="data.template.data.status.label" />
              </th>
              <th className="col-version">
                <FormattedMessage id="data.template.data.version" />
              </th>
              <th className="col-created-user">
                <FormattedMessage id="data.template.data.createdUser" />
              </th>
              <th className="col-created-date">
                <FormattedMessage id="data.template.data.createdDate" />
              </th>
              <th className="col-actions"></th>
            </tr>
          </thead>
          <tbody>
            {templates ? (
              <TableBody templates={templates} rowNumberInit={rowNumberInit} />
            ) : (
              <LoadingBody hasError={hasError} />
            )}
          </tbody>
        </Table>
      </div>
      {copyConfirm !== null ? (
        <CopyModel
          templateName={copyConfirm.name}
          machineService={copyConfirm.machineService}
          handleOk={(templateCopy, machineService) => {
            setCopyConfirm(null);
            handleTemplateCopy({
              originalId: copyConfirm.id,
              name: templateCopy,
              machineService: machineService,
            });
          }}
          handleClose={() => {
            setCopyConfirm(null);
          }}
        />
      ) : null}
    </>
  );
};

TemplateTable.propTypes = {
  rowNumberInit: PropTypes.number.isRequired,
  templates: PropTypes.array,
  hasError: PropTypes.bool.isRequired,
  handleTemplateCopy: PropTypes.func.isRequired,
  handleTemplateDelete: PropTypes.func.isRequired,
};
